import { type GuardStatus, PurchaseType, UserCredentials, type UserRequirement, useSettings } from '@capsicum/common';
import { env, ModalService, useRouter } from '@capsicum/common-web';
import {
	type FanTokenOfferingDrawerParams,
	FanTokenOfferingLimits,
	FTOAccessedViaAppLinkAtom,
	FTOHowToDrawerAtom,
	getPeggedAmount
} from '@capsicum/features/fan-token-offering/shared';
import { useRequirement } from '@capsicum/features/regulations/shared';
import {
	type FanTokenOfferingWithPartner,
	RequirementsPageCredentialsNeeded,
	useNavigateToGateway,
	useNCWBalance
} from '@capsicum/socios-core';
import { useCredentialsHandler, useDeniedRequirementsModal } from '@capsicum/socios-core-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { FanTokenOfferingDisclaimer } from '../ui/drawers/FanTokenOfferingDisclaimer';
import { FanTokenOfferingDrawer } from '../ui/drawers/FanTokenOfferingDrawer';
import { FanTokenOfferingHowToDrawer } from '../ui/drawers/FanTokenOfferingHowToDrawer';

/*
 * This hook is used to handle the buy button functionality in the fan token offering page.
 * It checks if :
 * - the cap limit is not reached
 * - user has accepted the disclaimer
 * - Credentials
 * - user has enough CHZ balance
 * - redirect to the buy page on gateway
 *
 * @param {FanTokenOfferingWithPartner} fanTokenOffering - object with fan token offering data.
 *
 * @returns buy - The function to handle the buy button click
 */
export const useFanTokenOfferingBuy = (fanTokenOffering: FanTokenOfferingWithPartner, testID: string) => {
	const { navigateToGateway } = useNavigateToGateway();
	const FTOHowToDrawerSeenIds = useAtomValue(FTOHowToDrawerAtom);
	const setFTOAccessedViaAppLink = useSetAtom(FTOAccessedViaAppLinkAtom);
	const { back, push, replace } = useRouter();
	const { chilizBalance } = useNCWBalance();
	const {
		settings: { repeatFtoDisclaimer, useInAppFlowForFTO }
	} = useSettings();

	const { showDeniedRequirementModal } = useDeniedRequirementsModal({ isLogoutModal: true, onDismiss: back });
	const { requirement } = useRequirement({
		requirementIdentifier: 'fto_crypto_disclaimer',
		showDeniedRequirementModal
	});

	const drawerParams: FanTokenOfferingDrawerParams = {
		capPerUser: fanTokenOffering.capPerUser,
		fanTokenShortCode: fanTokenOffering.fanToken.shortCode,
		walletBalance: chilizBalance?.amount,
		walletCurrencyShortCode: chilizBalance?.shortCode
	};

	const defaultConversionRate = parseFloat(
		getPeggedAmount(chilizBalance?.currencyId, fanTokenOffering.convertedPrices)
	);

	const navigateToFTO = () => {
		if (useInAppFlowForFTO) {
			setFTOAccessedViaAppLink(true);
			push(`/fan-token-offering/${fanTokenOffering.id}`);
		} else {
			navigateToGateway({
				gatewayWebLinkParams: {
					baseUrl: env.NEXT_PUBLIC_FTDP_GATEWAY_URL,
					fanTokenCurrencyId: fanTokenOffering.fanToken.id || '',
					fanTokenOfferingId: fanTokenOffering.id || '',
					fanTokenShortCode: fanTokenOffering.fanToken.shortCode || '',
					purchaseType: PurchaseType.FTO
				},
				navigate: replace
			});
		}
	};

	const proceedToFTOFlow = () => {
		const hasSeenDrawer = FTOHowToDrawerSeenIds.includes(fanTokenOffering.id);

		if (!hasSeenDrawer) {
			ModalService.addModal({
				detached: true,
				renderComponent: ({ close }) => (
					<FanTokenOfferingHowToDrawer
						closeModal={close}
						fanTokenOfferingId={fanTokenOffering?.id}
						shortCode={fanTokenOffering?.fanToken?.shortCode}
						testID={testID}
						onClick={navigateToFTO}
					/>
				)
			});

			return;
		}

		navigateToFTO();
	};

	// Check if user has Enough CHZ balance
	const checkUserBalance = (initialCredentialsStatus: GuardStatus) => {
		// user is able to buy 1 fan token
		if (!chilizBalance?.amount || chilizBalance?.amount < defaultConversionRate) {
			ModalService.addModal({
				detached: true,
				renderComponent: ({ close }) => (
					<FanTokenOfferingDrawer
						closeModal={close}
						drawerParams={drawerParams}
						testID={testID}
						type={FanTokenOfferingLimits.InsufficientBalance}
					/>
				)
			});

			return;
		}

		if (repeatFtoDisclaimer && initialCredentialsStatus !== 'denied') {
			ModalService.addModal({
				detached: true,
				renderComponent: ({ close }) => (
					<FanTokenOfferingDisclaimer
						closeModal={close}
						requirement={requirement as UserRequirement}
						testID={testID}
						onAgree={proceedToFTOFlow}
					/>
				),
				size: 'large'
			});

			return;
		}

		proceedToFTOFlow();
	};

	// Check if user has reached the cap limit
	const checkUserCap = () => {
		if (
			fanTokenOffering.capPerUser <= fanTokenOffering.statistics.fanTokenTotalAmount &&
			fanTokenOffering.capPerUser
		) {
			ModalService.addModal({
				detached: true,
				renderComponent: ({ close }) => (
					<FanTokenOfferingDrawer
						closeModal={close}
						drawerParams={drawerParams}
						testID={testID}
						type={FanTokenOfferingLimits.OverCap}
					/>
				)
			});

			return;
		}

		handleFanTokenOfferingBuyCredentials();
	};

	// Check disclaimer and credentials
	const { handleCredential: handleFanTokenOfferingBuyCredentials, status } = useCredentialsHandler({
		credentialCodes: [UserCredentials.VOTE_TOKEN_BUY_ORDER],
		onCredentialGranted: () => checkUserBalance(status),
		page: RequirementsPageCredentialsNeeded.fanTokenOfferingBuy
	});

	return { buy: checkUserCap };
};
